if (!customElements.get('wallify-frame-preview')) {
  class WallifyFramePreview extends HTMLElement {
    static preloadedSources = new Set();

    constructor() {
      super();
      this.handleChange = this.handleChange.bind(this);
      this.pendingFrame = null;
    }

    connectedCallback() {
      this.productRoot = this.closest('.product');
      this.productRoot?.addEventListener('change', this.handleChange, true);

      queueMicrotask(() => {
        if (!this.isConnected) return;

        const preserveInitialMedia =
          this.closest('gallery-carousel')?.dataset.wallifyPreserveInitialMedia === 'true';

        this.updateFrame({ updateGallery: !preserveInitialMedia });

        ['srcWhite', 'srcNaturalWood', 'srcDarkWood', 'srcBlack'].forEach((key) => {
          const source = this.dataset[key];

          if (!source || WallifyFramePreview.preloadedSources.has(source)) return;

          WallifyFramePreview.preloadedSources.add(source);

          const image = new Image();
          image.src = source;
        });
      });
    }

    disconnectedCallback() {
      this.productRoot?.removeEventListener('change', this.handleChange, true);
      this.productRoot = null;
      if (this.pendingFrame !== null) cancelAnimationFrame(this.pendingFrame);
      this.pendingFrame = null;
    }

    handleChange(event) {
      const picker = event.target.closest?.('variant-selects');

      if (!picker || picker.closest('.product') !== this.productRoot) return;
      if (this.pendingFrame !== null) return;

      this.pendingFrame = requestAnimationFrame(() => {
        this.pendingFrame = null;
        if (this.isConnected) this.updateFrame();
      });
    }

    updateFrame({ updateGallery = true } = {}) {
      const picker = this.productRoot?.querySelector('variant-selects');

      if (!picker) return;

      const containers = Array.from(picker.children).filter((child) =>
        child.classList.contains('product-form__input')
      );

      const container = containers[Number(this.dataset.frameOption)];

      if (!container) return;

      const select = container.querySelector('select');
      const radios = Array.from(
        container.querySelectorAll('input[type="radio"]')
      );

      const position = select
        ? select.selectedIndex
        : radios.findIndex((radio) => radio.checked);

      const key = [
        'none',
        'white',
        'natural-wood',
        'dark-wood',
        'black'
      ][position];

      if (!key) return;

      if (key === 'none') {
        this.dataset.frame = 'none';
        if (updateGallery) this.updateCarousel(picker, key);
        return;
      }

      this.dataset.frame = key;

      const selected = select
        ? select.selectedOptions[0]
        : radios[position];

      const label =
        selected?.dataset.wallifyDisplayValue ||
        selected?.value ||
        '';

      this.setAttribute(
        'aria-label',
        [this.dataset.productTitle, label].filter(Boolean).join(' — ')
      );

      if (updateGallery) this.updateCarousel(picker, key);
    }

    updateCarousel(picker, key) {
      if (
        this.classList.contains(
          'wallify-frame-preview--thumbnail'
        )
      ) {
        return;
      }

      const gallery = this.closest('gallery-carousel');
      const ownMediaId =
        this.closest('[data-media-id]')?.dataset.mediaId;

      if (
        !gallery ||
        !ownMediaId ||
        typeof gallery.updateCarouselImages !== 'function'
      ) {
        return;
      }

      if (key !== 'none') {
        gallery.updateCarouselImages(ownMediaId);
        return;
      }

      const unframedMediaId = this.dataset.unframedMediaId;

      if (
        unframedMediaId &&
        unframedMediaId !== ownMediaId
      ) {
        gallery.updateCarouselImages(unframedMediaId);
        return;
      }

      const containers = Array.from(picker.children).filter((child) =>
        child.classList.contains('product-form__input')
      );

      const selectedOptions = containers.map((container) => {
        const select = container.querySelector('select');

        if (select) return select.value;

        return Array.from(
          container.querySelectorAll('input[type="radio"]')
        ).find((radio) => radio.checked)?.value;
      });

      let variant = picker.currentVariant;

      if (
        !variant &&
        typeof picker.getVariantData === 'function'
      ) {
        variant = picker
          .getVariantData()
          .find((candidate) =>
            candidate.options?.every(
              (option, index) =>
                option === selectedOptions[index]
            )
          );
      }

      const variantMediaId = variant?.featured_media?.id;

      if (
        variantMediaId &&
        String(variantMediaId) !== ownMediaId
      ) {
        gallery.updateCarouselImages(variantMediaId);
        return;
      }

      const mainCarousel = gallery.querySelector(
        '[id^="Main-Carousel"]'
      );

      const fallbackSlide = Array.from(
        mainCarousel?.querySelectorAll(
          '.splide__slide[data-media-id]'
        ) || []
      ).find(
        (slide) => slide.dataset.mediaId !== ownMediaId
      );

      if (fallbackSlide?.dataset.mediaId) {
        gallery.updateCarouselImages(
          fallbackSlide.dataset.mediaId
        );
      }
    }
  }

  customElements.define(
    'wallify-frame-preview',
    WallifyFramePreview
  );
}
